import type { PropFootprint } from "../compile/footprint.ts";
import { FOOTPRINT_DEFAULT_CELL } from "../compile/footprint-cell.ts";
import { applyFootprintOverrides, footprintAfterChange, frameStandMarks, type FootprintOverride } from "../compile/footprint-override.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import type { FootprintLibraryView } from "./footprint-draft-write.ts";

/**
 * A footprint save the library refused as stale (P1.14-T57): the entry the save was worked out against has been
 * changed by another save since. The host throws `StaleFootprintError` from its saver, the panel that sees it offers
 * Reload, and Reload shows the entry the library holds now (`showStoredEntry`) in place of the held change.
 */

/**
 * The library's refusal of a save whose entry has moved since it was read. The message is the library's sentence.
 */
export class StaleFootprintError extends Error {
  /**
   * @param message The sentence the library gave.
   */
  public constructor(message: string) {
    super(message);
    this.name = "StaleFootprintError";
  }
}

/**
 * Whether a refusal is the library's stale refusal. Told by the error's class, never by its words.
 *
 * @param error Whatever a write threw.
 * @returns True for a `StaleFootprintError`.
 */
export function isStaleFootprint(error: unknown): error is StaleFootprintError {
  return error instanceof StaleFootprintError;
}

/**
 * The host's saver of one footprint override: it sends the change to the library and throws when the library refuses it,
 * with a `StaleFootprintError` when the entry has moved. A host that can re-read an entry gives `reload`, which shows the
 * library's entry for a frame in place of what the editor holds and keeps the entry as the base of the next save.
 */
export interface FootprintSaver {
  (packId: string, frameId: string, override: FootprintOverride | null): Promise<void>;
  readonly reload?: (packId: string, frameId: string) => Promise<void>;
}

/**
 * What showing an entry patches: the editor's footprints and marks for a frame.
 */
export type FootprintShown = Pick<FootprintLibraryView, "footprintOf" | "setFootprint" | "derivedFootprintOf" | "setMarks">;

/**
 * Shows a frame as the library's entry for it says: the footprint the entry's cells give (over the footprint the build
 * derives, or the one handed over for an entry whose band gives it), `none` as an empty footprint, and the Behind and In
 * front cells as the marks. The editor is patched as a write patches it (`footprintAfterChange`), so what the panel
 * draws after Reload is what it would draw after saving that entry.
 *
 * @param view The editor's footprints.
 * @param frame The frame.
 * @param size The frame's size in pixels.
 * @param cellPixels Pixels per collision cell the editor authors at.
 * @param entry The library's entry for the frame, or undefined when it holds none.
 * @param banded The footprint the art gives at the entry's band, for an entry that holds a band and no cells.
 */
export function showStoredEntry(view: FootprintShown, frame: string, size: { readonly w: number; readonly h: number }, cellPixels: number, entry: FootprintOverride | undefined, banded?: PropFootprint): void {
  const derived = view.derivedFootprintOf(frame);
  const base = entry !== undefined && entry !== "none" && entry.cells === undefined && banded !== undefined ? banded : derived;
  const shown = entry === undefined ? undefined : footprintAfterChange(base, entry, size, cellPixels);
  view.setFootprint(frame, shown ?? base ?? null);
  const behind = entry === undefined || entry === "none" ? [] : (entry.behind ?? []);
  const front = entry === undefined || entry === "none" ? [] : (entry.front ?? []);
  view.setMarks?.(frame, behind.length === 0 && front.length === 0 ? null : { cell: entry === undefined || entry === "none" ? FOOTPRINT_DEFAULT_CELL : (entry.cell ?? FOOTPRINT_DEFAULT_CELL), behind, front });
}

/**
 * What comparing an entry with the editor reads: the footprint it shows for a frame, the one the build derives, and the
 * marks the library gave it apart from any a panel previews.
 */
export type FootprintCompared = Pick<FootprintLibraryView, "footprintOf" | "derivedFootprintOf" | "libraryMarksOf" | "marksOf">;

/**
 * Whether the editor shows a frame as a library entry would make it, by the compile's own rules (`applyFootprintOverrides`
 * over the derived footprint, `frameStandMarks`): the same blocking runs and passage, and the same Behind and In front cells
 * at the same cell, in any order. A flag, a band, a surface or a timing is not compared, as the shape's digest leaves them
 * out (`footprintShapeDigest`). A host uses it to tell whether the digest of an entry it read names what a panel shows,
 * before a save names that digest (P1.14-T57): the editor shows the last compile, which may be older than the entry.
 *
 * @param view The editor's footprints.
 * @param frame The frame.
 * @param size The frame's size in pixels, or undefined when the atlas has none. The footprint shown sizes the entry when
 *   there is one, since it holds the size the compile laid the entry's cells on.
 * @param cellPixels Pixels per collision cell the editor authors at.
 * @param entry The library's entry for the frame, or undefined when it holds none.
 * @returns True when the frame is shown as the entry makes it.
 */
export function shownMatchesEntry(view: FootprintCompared, frame: string, size: { readonly w: number; readonly h: number } | undefined, cellPixels: number, entry: FootprintOverride | undefined): boolean {
  const shown = view.footprintOf(frame);
  const derived = view.derivedFootprintOf(frame);
  const sized = shown === undefined ? size : { w: shown.w, h: shown.h };
  const made = entry === undefined ? derived : applyFootprintOverrides(derived === undefined ? {} : { [frame]: derived }, { [frame]: entry }, () => sized, cellPixels)[frame];
  const marks = entry === undefined ? undefined : frameStandMarks({ [frame]: entry }).get(frame);
  const shownMarks = view.libraryMarksOf === undefined ? view.marksOf?.(frame) : view.libraryMarksOf(frame);
  return sameShape(made, shown) && sameMarks(marks, shownMarks);
}

/**
 * Whether two footprints block and open the same cells: their size, base line, runs and passage, not their surface.
 *
 * @param one A footprint, or undefined for none.
 * @param other Another.
 * @returns True when they are the same shape.
 */
function sameShape(one: PropFootprint | undefined, other: PropFootprint | undefined): boolean {
  if (one === undefined || other === undefined) {
    return one === other;
  }
  return one.w === other.w && one.h === other.h && one.base === other.base && JSON.stringify(one.runs) === JSON.stringify(other.runs) && JSON.stringify(one.passage ?? []) === JSON.stringify(other.passage ?? []);
}

/**
 * Whether two sets of marks hold the same Behind and In front cells at the same cell, in any order.
 *
 * @param one Marks, or undefined for none.
 * @param other Other marks.
 * @returns True when they are the same.
 */
function sameMarks(one: FrameMarks | undefined, other: FrameMarks | undefined): boolean {
  if (one === undefined || other === undefined) {
    return one === other;
  }
  const keys = (cells: readonly (readonly [number, number])[]): string => cells.map(([x, y]) => `${x},${y}`).sort().join(";");
  return one.cell === other.cell && keys(one.behind) === keys(other.behind) && keys(one.front) === keys(other.front);
}
